import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'
import YouTube, { YouTubeEvent, YouTubePlayer } from 'react-youtube'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Section from '@/components/base/Section'
import Spinner from '@/components/base/Spinner'
import SVGIcon from '@/components/base/SVGIcon'
import useInView from '@/hooks/useInView'
import useYoutubeThumbnail from '@/hooks/useYoutubeThumbnail'

import { cn } from '@/lib/utils'
import { WPImage, WPVideo } from '@/types'

type MediaVideoProps = HTMLAttributes<HTMLElement> &
  Partial<{
    video_type: 'video' | 'youtube' | 'video_url' | 'video_external'
    video: WPVideo
    video_source_url: string
    video_external_url: string
    youtube_id: string
    poster: WPImage
  }>

const MediaVideo = ({
  video_type,
  video,
  video_source_url,
  video_external_url,
  youtube_id,
  poster,
  className,
  ...props
}: MediaVideoProps) => {
  const [isActive, setIsActive] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isBuffering, setIsBuffering] = useState(false)
  const videoPlayer = useRef<HTMLVideoElement>(null)
  const youtubePlayer = useRef<YouTubePlayer>(null)
  const youtubePoster = useYoutubeThumbnail(youtube_id!)
  const root = useRef<HTMLDivElement>(null)
  const inView = useInView({ ref: root })

  const playVideo = useCallback(() => {
    setIsBuffering(true)
    if (video_type === 'video' || video_type === 'video_url') {
      videoPlayer.current?.play()
    } else if (video_type === 'youtube') {
      youtubePlayer.current?.playVideo()
    }
  }, [video_type])

  const pauseVideo = useCallback(() => {
    if (video_type === 'video' || video_type === 'video_url') {
      videoPlayer.current?.pause()
    } else if (video_type === 'youtube') {
      youtubePlayer.current?.pauseVideo()
    }
  }, [video_type])

  const handleStateChange = useCallback((event: YouTubeEvent) => {
    event.data === 1 && setIsPlaying(true) // playing
    event.data === 2 && setIsPlaying(false) // paused
  }, [])

  const handleVideoReady = (event: YouTubeEvent<any>) => {
    youtubePlayer.current = event.target
  }

  useEffect(() => {
    if (!isPlaying) return
    setIsBuffering(false)
    setIsActive(true)
  }, [isPlaying])

  useEffect(() => {
    if (inView) return
    setIsBuffering(false)
    setIsActive(false)
    pauseVideo()
  }, [inView, pauseVideo])

  return (
    <Section ref={root} className={cn([className])} {...props}>
      <Container>
        <Grid>
          <div className="col-span-4 md:col-span-6 lg:col-span-12">
            <div
              className={cn([
                'aspect-h-9 aspect-w-16 relative h-0 w-full rounded-[3rem] bg-dark-blue',
                'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:rounded-[3rem] after:bg-black/50',
                {
                  'after:hidden': isActive
                }
              ])}
            >
              {video_type === 'video' ? (
                <video
                  ref={videoPlayer}
                  controls
                  preload="auto"
                  className="rounded-[3rem]"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  <source src={video?.url} type={video?.mimeType} />
                </video>
              ) : video_type === 'youtube' ? (
                <div className="[&_iframe]:absolute [&_iframe]:left-0 [&_iframe]:top-0 [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:rounded-[3rem]">
                  <YouTube
                    videoId={youtube_id}
                    onStateChange={handleStateChange}
                    onReady={handleVideoReady}
                    opts={{
                      playerVars: {
                        autoplay: 0,
                        controls: 1,
                        showinfo: 0,
                        color: 'white',
                        rel: 0,
                        fs: 0,
                        modestbranding: 1
                      },
                      host: 'https://www.youtube-nocookie.com'
                    }}
                  />
                </div>
              ) : video_type === 'video_url' ? (
                <video
                  ref={videoPlayer}
                  controls
                  preload="auto"
                  className="rounded-[3rem]"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  <source src={video_source_url} />
                </video>
              ) : null}

              {!isActive ? (
                <Image
                  source={poster || { url: youtubePoster }}
                  fill
                  fit="cover"
                  className="rounded-[3rem]"
                />
              ) : null}

              {!isActive && video_type !== 'video_external' ? (
                <button
                  onClick={playVideo}
                  className={cn([
                    'absolute left-1/2 top-1/2 z-10 h-[6rem] w-[6rem] -translate-x-1/2 -translate-y-1/2 rounded-full',
                    'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:rounded-full before:bg-white/20 before:backdrop-blur-sm',
                    'before:transition-transform before:duration-300 before:ease-out-back',
                    'flex items-center justify-center',
                    'hover:before:scale-[1.05]'
                  ])}
                >
                  <span className="sr-only">Play</span>
                  {isBuffering ? (
                    <Spinner
                      variant="medium"
                      className="[&>svg]:stroke-white"
                    />
                  ) : (
                    <SVGIcon
                      symbol="play-icon"
                      className="relative h-[6rem] w-[6rem] fill-white"
                    />
                  )}
                </button>
              ) : null}
              {video_type === 'video_external' ? (
                <a
                  href={video_external_url}
                  target="_blank"
                  className={cn([
                    'absolute left-1/2 top-1/2 z-10 h-[6rem] w-[6rem] -translate-x-1/2 -translate-y-1/2 rounded-full',
                    'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:rounded-full before:bg-white/20 before:backdrop-blur-sm',
                    'before:transition-transform before:duration-300 before:ease-out-back',
                    'flex items-center justify-center',
                    'hover:before:scale-[1.05]'
                  ])}
                >
                  <span className="sr-only">Play</span>
                  <SVGIcon
                    symbol="play-icon"
                    className="relative h-[6rem] w-[6rem] fill-white"
                  />
                </a>
              ) : null}
            </div>
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default MediaVideo
